import React, { useState, useRef, useEffect } from 'react';
import { View, StyleSheet, Text, TouchableOpacity, Image, Dimensions, Animated, Linking, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import PlatformDetector from '../../../../utils/PlatformDetector';
import { useBanners } from '../../../BannerManager';
import { BACKEND_URL, getViewportDimensions } from '../../../../Config/Const';
import { globalStyles } from '../../../../styles/globalStyles';
import CarouselContainer from '../../../Common/CarouselContainer';

// Importar CSS para prevenir scroll horizontal en web
if (Platform.OS === 'web') {
  // Importar el CSS solo en entorno web
  try {
    // @ts-ignore
    require('../../../../styles/preventHorizontalScroll.css');
  } catch (e) {
    console.warn('No se pudo cargar el CSS para prevenir scroll horizontal');
  }
}

// Dimensiones para las tarjetas y espaciado entre ellas
const CARD_SPACING = 15; // Espacio entre tarjetas

// Función para calcular dimensiones responsivas
const getResponsiveCardDimensions = (width) => {
  const isMobileWeb = Platform.OS === 'web' && width < 768;
  const isMobile = PlatformDetector.isMobile() || isMobileWeb;
  
  // Calcular el ancho de las tarjetas según el tamaño de la pantalla
  let cardWidth;
  let cardsPerScreen;
  let cardHeight;
  
  if (width < 400) {
    // Móvil extra pequeño
    cardWidth = width - 30;
    cardsPerScreen = 1;
    cardHeight = 120;
  } else if (width < 576) {
    // Móvil pequeño
    cardWidth = width - 30;
    cardsPerScreen = 1;
    cardHeight = 130;
  } else if (width < 768) {
    // Móvil/Tablet pequeña
    cardWidth = width * 0.85;
    cardsPerScreen = 1;
    cardHeight = 140;
  } else if (width < 992) {
    // Tablet
    cardWidth = (width - 60) / 2;
    cardsPerScreen = 2;
    cardHeight = 140;
  } else if (width < 1200) {
    // Desktop pequeño
    cardWidth = (width - 80) / 3;
    cardsPerScreen = 3;
    cardHeight = 150;
  } else if (width < 1400) {
    // Desktop mediano
    cardWidth = (width - 100) / 4;
    cardsPerScreen = 4;
    cardHeight = 150;
  } else {
    // Desktop grande
    cardWidth = Math.min((width - 120) / 5, 320);
    cardsPerScreen = 5;
    cardHeight = 150;
  }
  
  const snapInterval = isMobileWeb ? width : cardWidth + CARD_SPACING;
  
  return { cardWidth, cardsPerScreen, cardHeight, snapInterval, isMobileWeb, isMobile };
};

const BannerBancario = () => {
  const navigation = useNavigation();
  const scrollViewRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const fadeAnim = useRef(new Animated.Value(1)).current;
  
  // Estado para dimensiones responsivas
  const initialWidth = Platform.OS === 'web' && typeof window !== 'undefined'
    ? window.innerWidth
    : Dimensions.get('window').width;
  const [viewportWidth, setViewportWidth] = useState(initialWidth);
  const [dimensions, setDimensions] = useState(() => getResponsiveCardDimensions(initialWidth));
  
  // Efecto para manejar cambios de tamaño de ventana
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleResize = () => {
        const newWidth = window.innerWidth;
        setViewportWidth(newWidth);
        setDimensions(getResponsiveCardDimensions(newWidth));
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);
  
  // Obtener los banners del contexto
  const { banners: allBanners, loading, error, isWeb } = useBanners();

  // Obtener los banners de la fila 3 (BannerBancario)
  const fila3Banners = allBanners.fila3 || [];
  // Filtrar banners que tengan imagen web (usada tanto en web como en mobile)
  const filteredBanners = fila3Banners.filter(banner => !!banner.imagenWeb);
  
  // Verificar si debemos mostrar el componente (solo si hay banners filtrados o estamos cargando)
  const shouldRender = (filteredBanners.length > 0) || loading;
  
  // Datos de las promociones - usar banners de la API si están disponibles
  // Siempre usar imagenWeb tanto en web como en mobile
  const promociones = filteredBanners.length > 0 
    ? filteredBanners.map(banner => {
          // Asegurarse de que la URL sea absoluta y esté bien formateada
          let imageUrl = banner.imagenWeb;
          return {
            id: banner.codBanner,
            image: imageUrl, // Siempre usar imagen web
            titulo: banner.titulo,
            link: banner.link,
            isApiImage: true // Flag para identificar imágenes de la API
          };
        })
    : loading ? [] : [];
  
  // Efecto de fade-in al montar el componente
  useEffect(() => {
    Animated.sequence([
      Animated.timing(fadeAnim, {
        toValue: 0.7,
        duration: 0,
        useNativeDriver: Platform.OS !== 'web', // Solo usar en plataformas nativas
      }),
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 500,
        useNativeDriver: Platform.OS !== 'web', // Solo usar en plataformas nativas
      })
    ]).start();
  }, []);
  
  // Auto-carrusel cada 3 segundos
  useEffect(() => {
    if (promociones.length > 0) {
      const interval = setInterval(() => {
        let nextIndex = currentIndex + 1;
        
        // Si llegamos al final, volver al principio (carrusel infinito)
        if (nextIndex >= promociones.length) {
          nextIndex = 0;
        }
        
        // Usar snapInterval como unidad de scroll
        const scrollUnit = dimensions.snapInterval;
        
        // Verificar que el scrollViewRef existe antes de intentar scrollear
        if (scrollViewRef.current) {
          scrollViewRef.current.scrollTo({
            x: nextIndex * scrollUnit,
            animated: true
          });
          setCurrentIndex(nextIndex);
        }
      }, 3000);
      
      return () => clearInterval(interval);
    }
  }, [promociones.length, currentIndex]);

  const handlePromoClick = (promo) => {
    // Animación de fade-out al hacer clic
    Animated.timing(fadeAnim, {
      toValue: 0.8,
      duration: 200,
      useNativeDriver: Platform.OS !== 'web', // Solo usar en plataformas nativas
    }).start(() => {
      // Si es un banner de la API
      if (promo.id) {
        // En web, construimos URL con parámetros y usamos history API
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          // Construir URL con parámetros - siempre incluir todos los parámetros
          const urlParams = new URLSearchParams();
          
          // Siempre incluir D, C, S como 0 (por defecto)
          urlParams.append('D', 0);
          urlParams.append('C', 0);
          urlParams.append('S', 0);
          
          // Agregar codBanner usando el ID del banner
          urlParams.append('codBanner', promo.id);
          urlParams.append('searchText', '');
          
          const url = `/productos?${urlParams.toString()}`;
          
          // Verificar si ya estamos en /productos
          const isOnProductsPage = window.location.pathname === '/productos';
          
          if (isOnProductsPage) {
            window.history.pushState({}, '', url);
            window.dispatchEvent(new Event('navigationChange'));
          } else {
            window.location.href = url;
          }
        } else if (promo.link) {
          // En móvil, abrir el link externo
          Linking.openURL(promo.link);
        }
      } else if (promo.link) {
        // Si no tiene ID pero tiene link, abrir el link directamente
        Linking.openURL(promo.link);
      } else if (promo.navigateTo) {
        // Si es un banner estático con navegación interna
        navigation.navigate(promo.navigateTo, promo.params);
      }
      
      // Restaurar opacidad después de navegar
      setTimeout(() => {
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: Platform.OS !== 'web', // Solo usar en plataformas nativas
        }).start();
      }, 300);
    });
  };

  const scrollToNext = () => {
    // Calcular cuántas tarjetas avanzar (1 en móvil, más en desktop)
    const scrollAmount = dimensions.isMobileWeb || dimensions.isMobile ? 1 : Math.floor(dimensions.cardsPerScreen / 2);
    // Usar SNAP_INTERVAL como unidad de scroll
    const scrollUnit = dimensions.snapInterval;
    
    if (currentIndex < promociones.length - scrollAmount) {
      // Avanzar normalmente
      const newIndex = currentIndex + scrollAmount;
      if (scrollViewRef.current) {
        scrollViewRef.current.scrollTo({
          x: newIndex * scrollUnit,
          animated: true
        });
        setCurrentIndex(newIndex);
      }
    } else {
      // Volver al inicio (carrusel infinito)
      if (scrollViewRef.current) {
        scrollViewRef.current.scrollTo({
          x: 0,
          animated: true
        });
        setCurrentIndex(0);
      }
    }
  };

  const scrollToPrev = () => {
    // Calcular cuántas tarjetas retroceder (1 en móvil, más en desktop)
    const scrollAmount = dimensions.isMobileWeb || dimensions.isMobile ? 1 : Math.floor(dimensions.cardsPerScreen / 2);
    // Usar SNAP_INTERVAL como unidad de scroll
    const scrollUnit = dimensions.snapInterval;
    
    if (currentIndex > 0) {
      // Retroceder normalmente
      const newIndex = Math.max(0, currentIndex - scrollAmount);
      if (scrollViewRef.current) {
        scrollViewRef.current.scrollTo({
          x: newIndex * scrollUnit,
          animated: true
        });
        setCurrentIndex(newIndex);
      }
    } else {
      // Ir al final (carrusel infinito)
      const newIndex = promociones.length - Math.min(scrollAmount, promociones.length);
      if (scrollViewRef.current) {
        scrollViewRef.current.scrollTo({
          x: newIndex * scrollUnit,
          animated: true
        });
        setCurrentIndex(newIndex);
      }
    }
  };

  const handleScroll = (event) => {
    if (!isScrolling) {
      const contentOffset = event.nativeEvent.contentOffset;
      // Usar viewportWidth como unidad de scroll en móvil web para evitar problemas de cálculo
      const scrollUnit = dimensions.isMobileWeb ? viewportWidth : (dimensions.cardWidth + 10);
      const index = Math.round(contentOffset.x / scrollUnit);
      
      // Asegurarse de que el índice esté dentro de los límites
      if (index >= 0 && index < promociones.length && index !== currentIndex) {
        setCurrentIndex(index);
      }
    }
  };
  
  // Ya no necesitamos estas funciones porque las maneja CarouselContainer

  // Solo renderizar si hay banners para mostrar o si estamos cargando
  if (!shouldRender) {
    return null;
  }
  
  return (
    <Animated.View style={[styles.container, { opacity: fadeAnim, marginBottom: 30 }]}>
      
      <View style={styles.carouselWrapper}>
        {/* Botón para moverse a la izquierda - siempre visible con carrusel infinito */}
        <TouchableOpacity style={styles.navButtonLeft} onPress={scrollToPrev}>
          <MaterialCommunityIcons name="chevron-left" size={28} color="#fff" />
        </TouchableOpacity>
        
        <View style={styles.carouselContainerOuter}>
          {/* ScrollView horizontal para las imágenes */}
          <CarouselContainer
            ref={scrollViewRef}
            snapToInterval={dimensions.snapInterval}
            pagingEnabled={dimensions.isMobileWeb} // Activar paginación en móvil web para evitar scroll parcial
            onMomentumScrollEnd={handleScroll} // Usar onMomentumScrollEnd en lugar de onScroll
            onScrollBeginDrag={() => setIsScrolling(true)}
            onScrollEndDrag={() => setIsScrolling(false)}
            contentContainerStyle={[styles.scrollContent, 
              { 
                // Centrar las tarjetas en la pantalla con padding adecuado
                paddingHorizontal: dimensions.isMobileWeb ? 15 : 40
              }
            ]}
          >
            {promociones.map((promo, index) => (
              <Animated.View key={`promo-${promo.id || index}`} style={[styles.promoCard, { 
                opacity: fadeAnim, 
                width: dimensions.cardWidth,
                height: dimensions.cardHeight,
                marginHorizontal: CARD_SPACING / 2,
                // Mantener bordes redondeados en todos los dispositivos para consistencia visual
                borderRadius: 8
              }]}>
                <TouchableOpacity
                  style={{ flex: 1 }}
                  onPress={() => handlePromoClick(promo)}
                  activeOpacity={0.9}
                >
                  {promo.isApiImage ? (
                    <>
                      <Image
                        source={{ uri: promo.image }}
                        style={{ width: '100%', height: '100%', backgroundColor: '#f0f0f0' }}
                        resizeMode="cover"
                      />
                    </>
                  ) : (
                    <Image
                      source={promo.image}
                      style={{ width: '100%', height: '100%', backgroundColor: '#f0f0f0' }}
                      resizeMode="cover"
                    />
                  )}
                </TouchableOpacity>
              </Animated.View>
            ))}
          </CarouselContainer>
        </View>
        
        {/* Botón para moverse a la derecha - siempre visible con carrusel infinito */}
        <TouchableOpacity style={styles.navButtonRight} onPress={scrollToNext}>
          <MaterialCommunityIcons name="chevron-right" size={28} color="#fff" />
        </TouchableOpacity>
      </View>

    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 10,
    marginHorizontal: 0,
    width: '100%',
    
  },
  // Se eliminó sectionTitle que no se usa
  carouselWrapper: {
    position: 'relative',
    width: '100%',
    alignItems: 'center',
    
    
  },
  carouselContainerOuter: {
    width: '100%',
    paddingVertical: 20,
    overflow: 'hidden',
    minHeight: 150,
  },
  scrollContent: {
    alignItems: 'center',
    justifyContent: 'flex-start', // Alinear al inicio para mejor posicionamiento
    flexDirection: 'row', // Asegurar que las tarjetas se muestren en fila
    flexGrow: 1,
  },
  // Se eliminaron cardContainer y cardImage que no se usan
  navButtonLeft: {
    position: 'absolute',
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 20,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.2,
          shadowRadius: 1.5,
          elevation: 2,
        }
    ),
    zIndex: 2,
    left: 10,
    top: 'calc(55%)',
    transform: [],
  },
  navButtonRight: {
    position: 'absolute',
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 20,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.2,
          shadowRadius: 1.5,
          elevation: 2,
        }
    ),
    zIndex: 2,
    right: 10,
    top: 'calc(55%)',
    transform: [],
  },
  // Se eliminaron estilos de header y botones de navegación que no se usan
  carouselContainer: {
    height: 150, // Altura del contenedor de 150px
    width: '100%',
    alignItems: 'center',
    paddingVertical: 25, // Padding vertical aumentado para centrar tarjetas en contenedor más grande
  },
  // Se eliminó scrollContainer que no se usa
  promoCard: {
    // Width y height ahora se establecen dinámicamente
    marginVertical: 5,
    backgroundColor: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 3px 6px rgba(0,0,0,0.12)' }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 3 },
          shadowOpacity: 0.12,
          shadowRadius: 6,
          elevation: 4,
        }
    ),
  },
  

});

export default BannerBancario;
